/**
 * Single fetch wrapper for the whole client. Everything with a `/api/*` URL
 * goes through here so we get:
 *   - Bearer token attached automatically
 *   - httpOnly refresh cookie sent on every request (credentials: 'include')
 *   - 401 → single refresh attempt → retry once
 *   - Non-2xx → typed ApiError thrown
 *
 * No component ever calls fetch() directly.
 */

import type { ApiError } from '@fuelsync/common';
import { env } from '@/env';
import { useAuthStore } from '@/features/auth/store';

const BASE = env.VITE_API_BASE_URL || '/api';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Skip the automatic refresh-on-401 dance. Used by the refresh call itself. */
  skipAuthRetry?: boolean;
}

export class ApiClientError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId: string | undefined;
  readonly details: unknown;

  constructor(payload: ApiError, status: number) {
    // Coerce non-string error shapes (Vercel's own function-error envelopes,
    // upstream proxies, misbehaving handlers) so the UI never renders
    // "[object Object]" and we always see something diagnostic.
    const msg =
      typeof payload.error === 'string'
        ? payload.error
        : payload.error
          ? JSON.stringify(payload.error)
          : `Request failed with ${status}`;
    super(msg);
    this.name = 'ApiClientError';
    this.code = payload.code;
    this.status = status;
    this.requestId = payload.requestId;
    this.details = payload.details;
  }
}

async function doFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, skipAuthRetry } = opts;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const token = useAuthStore.getState().accessToken;
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    credentials: 'include', // send refresh cookie
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  // 401 → try refresh once, then retry.
  if (res.status === 401 && !skipAuthRetry) {
    const refreshed = await tryRefresh();
    if (refreshed) return doFetch<T>(path, { ...opts, skipAuthRetry: true });
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : undefined;

  if (!res.ok) {
    const payload =
      json && typeof json === 'object' && 'error' in json
        ? (json as ApiError)
        : { error: `Request failed with ${res.status}`, code: 'UNKNOWN', requestId: '' };
    throw new ApiClientError(payload, res.status);
  }

  return json as T;
}

let refreshInflight: Promise<boolean> | null = null;
async function tryRefresh(): Promise<boolean> {
  if (refreshInflight) return refreshInflight;
  refreshInflight = (async () => {
    try {
      // Impersonation tabs must never refresh via the refresh cookie: the
      // browser would send the ADMIN's cookie (same-origin) and we'd get
      // the admin's access token back, silently upgrading the tab from
      // read-only impersonation to full admin — while the banner still
      // shows "read-only". Fail closed instead; the tab drops to /login
      // and the admin can re-impersonate from the console if needed.
      if (useAuthStore.getState().isImpersonating) {
        useAuthStore.getState().clear();
        return false;
      }
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        useAuthStore.getState().clear();
        return false;
      }
      const json = (await res.json()) as { accessToken: string; accessTokenExpiresAt: string };
      useAuthStore.getState().setAccessToken(json.accessToken, json.accessTokenExpiresAt);
      return true;
    } catch {
      useAuthStore.getState().clear();
      return false;
    } finally {
      refreshInflight = null;
    }
  })();
  return refreshInflight;
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    doFetch<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    doFetch<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    doFetch<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    doFetch<T>(path, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    doFetch<T>(path, { ...opts, method: 'DELETE' }),
};
